import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useState } from 'react';
import ReactDom from 'react-dom';
import { useForm } from 'react-hook-form';

import { useStopwatch } from 'react-timer-hook';
import * as yup from 'yup';

import { Icon as BlueIcon } from '@blueprintjs/core';

import BackIconImage from '@assets/svg/back-icon.svg';
import FileIcon from '@assets/svg/file-document.svg';

import { useMutation, useQueryClient } from 'react-query';
import { NavigationContext } from '@layout/navigation/navigation';
import { useHistory } from 'react-router-dom';
import {
  fileUploadInvoice,
  IFileUploadInvoice,
} from '@services/queries/onboarding/fileUploadInvoice';
import { importFile } from '@services/mixpanel/trackers/Onboarding';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Back,
  Confirm,
  Row,
  Label,
  Line,
  Input,
  SelectFile,
  Form,
  Divider,
  SpacingDivider,
  ProgressContainer,
  FileImage,
  HolderDiv,
  TimerText,
  FileNameText,
  DescriptionText,
} from './ImportInvoice.styles';
import { InvoiceImportStatusDialog } from './components/InvoiceImportStatusDialog/InvoiceImportStatusDialog';

interface IImportInvoice {
  open: boolean;
  onClose: () => void;
}

interface IFormData {
  file?: FileList;
}

interface IError {
  response: {
    data: {
      message: string;
    };
  };
}

export const ImportInvoice = ({ open, onClose }: IImportInvoice) => {
  const portalDiv = document.getElementById('portal');

  const history = useHistory();

  const { studioId, studioName, userName, userEmail } =
    useContext(NavigationContext);

  const queryClient = useQueryClient();

  const [topic, setTopic] = useState<'user' | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [labelText, setLabelText] = useState<string>('Importing');
  const [descriptionText, setDescriptionText] = useState<string>(
    'Keep this window open to see the results.',
  );
  const [newFileId] = useState<string>('');
  const [errorFile, setErrorFile] = useState<string>('');

  const [progress, setProgress] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);

  const [showInvoiceUplaodStatus, setShowInvoiceUploadStatus] =
    useState<boolean>(false);

  const { seconds, minutes, start, pause } = useStopwatch({ autoStart: false });

  const schema = yup
    .object({
      file: yup.mixed().required(),
    })
    .required();

  const mutationFileUpload = useMutation(
    async (result: IFileUploadInvoice) => {
      await fileUploadInvoice(result);
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          queryClient.invalidateQueries(['onboarding', 'files']);
        }, 2000);
        queryClient.invalidateQueries(['onboarding', 'files']);

        importFile(studioName, 'invoice', fileType, true);

        setShowInvoiceUploadStatus(true);
      },
      onError: ({ response }: IError) => {
        importFile(studioName, 'invoice', fileType, false);

        setLabelText('Error');
        setDescriptionText(response.data.message);

        pause();
        setDisabled(false);
      },
    },
  );

  const { register, handleSubmit } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const done = () => {
    history.push(`/onboarding/${newFileId}`);
    onClose();
  };

  const onSubmit = (data: IFormData) => {
    if (!mutationFileUpload.isLoading) {
      const result: any = {
        file: data.file?.[0],
        studioId,
        email: userEmail,
        studioName,
        userName,
      };

      mutationFileUpload.mutate(result);
    }

    setProgress(true);
    start();
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              {topic && (
                <>
                  <Back
                    src={BackIconImage}
                    alt="back"
                    onClick={() => setTopic(null)}
                  />
                  <SpacingDivider />
                </>
              )}
              <Title>Import Invoice</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            {progress ? (
              <ProgressContainer>
                <Label status={labelText}>{labelText}</Label>

                <Divider height="8px" />

                <Row className="progressRow">
                  <HolderDiv>
                    <FileImage src={FileIcon} alt="FileIcon" />
                    <FileNameText className="file-name" limitOfLines={1}>
                      {fileName}
                    </FileNameText>
                  </HolderDiv>
                  <TimerText>{`${minutes < 10 ? `0${minutes}` : minutes}:${
                    seconds < 10 ? `0${seconds}` : seconds
                  }`}</TimerText>
                </Row>

                <Divider height="24px" />

                <DescriptionText>{descriptionText}</DescriptionText>

                <Line>
                  <Confirm
                    disabled={disabled || labelText === 'Error'}
                    onClick={done}
                  >
                    Import
                  </Confirm>
                </Line>
              </ProgressContainer>
            ) : (
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Label>File</Label>
                <Row className="pictureInputsWithLegend">
                  <Input
                    type="text"
                    placeholder="Select a .zip or .pdf file"
                    value={fileName}
                    readOnly
                    isError={!!errorFile}
                  />

                  <SelectFile htmlFor="upload" isError={!!errorFile}>
                    Select File
                  </SelectFile>
                  <input
                    type="file"
                    id="upload"
                    accept=".zip, .pdf"
                    hidden
                    {...register('file')}
                    onChange={e => {
                      const newFile = e.target.files?.[0];
                      if (newFile) {
                        register('file').onChange(e);
                        setFileName(e.target.files?.[0]?.name || '');
                        setFileType(newFile.type);

                        if (
                          ![
                            'application/x-zip-compressed',
                            'application/zip',
                            'application/pdf',
                          ].includes(newFile.type)
                        ) {
                          setErrorFile('Please select a .zip or .pdf file.');
                        } else if (newFile.size >= 5242880) {
                          setErrorFile('Please select a file under 5MB.');
                        } else {
                          setErrorFile('');
                        }
                      }
                    }}
                  />
                </Row>

                {!!errorFile && (
                  <DescriptionText status="Error">{errorFile}</DescriptionText>
                )}

                <Line>
                  <Confirm type="submit" disabled={!fileName || !!errorFile}>
                    Import
                  </Confirm>
                </Line>
              </Form>
            )}

            <InvoiceImportStatusDialog
              open={showInvoiceUplaodStatus}
              onClose={onClose}
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
